import { useFormikContext } from "formik";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Autocomplete } from "../../Autocomplete";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { listInsights } from "../../../services/insights";
import { listForms } from "../../../services/forms";
import {
  CampaignAwarded,
  CreateCampaignAwardedData,
} from "../../../types/campaignAwarded";
import Skeleton from "@mui/material/Skeleton";

type PublicProps = {
  data?: CampaignAwarded;
};

export function Public({ data }: PublicProps) {
  const formik = useFormikContext<CreateCampaignAwardedData>();

  const insights = useQuery("insights", listInsights, {
    enabled: formik.values.public === "insight",
    onSuccess: (response) => {
      if (data?.insight_id) {
        const findTarget = response?.find(
          (resp) => resp.idListProspectei === data?.insight_id
        );
        formik.setFieldValue("target", {
          ...findTarget,
          name: findTarget?.nameList,
        });
      }
    },
  });

  const forms = useQuery("forms", listForms, {
    enabled: formik.values.public === "form",
    onSuccess: (response) => {
      if (data?.form_id) {
        const findTarget = response?.find(
          (resp) => resp.id_formulario === data?.form_id
        );
        formik.setFieldValue("target", {
          ...findTarget,
          name: findTarget.nameForm,
        });
      }
    },
  });

  const publicType = useMemo(() => {
    if (formik.values.public === "insight") {
      return "Audiências";
    } else {
      return "Formulários";
    }
  }, [formik.values.public]);

  const publicOptions: Array<any> = useMemo(() => {
    if (formik.values.public === "insight") {
      return insights.data?.map((insight) => ({
        ...insight,
        name: insight.nameList,
      }));
    } else {
      return forms.data
        ?.filter((form) => form.typeForm === "CUSTOMER_LIKES")
        ?.map((form) => ({ ...form, name: form.nameForm }));
    }
  }, [formik.values.public, insights.data, forms.data]);

  return (
    <Stack spacing={4}>
      <Typography color="primary">Público</Typography>
      <Stack direction="row" spacing={2}>
        <RadioGroup
          row
          value={formik.values.public}
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          onChange={(_, value) => {
            formik.setFieldValue("target", null);
            formik.setFieldValue("public", value);
          }}
        >
          <FormControlLabel
            value="insight"
            control={<Radio />}
            label="Audiências"
          />
          <FormControlLabel
            value="form"
            control={<Radio />}
            label="Formulários"
          />
        </RadioGroup>
      </Stack>

      {insights.isFetching || forms.isFetching ? (
        <Skeleton height={60} variant="text" />
      ) : (
        <Autocomplete
          size="small"
          label={publicType}
          loading={insights.isFetching || forms.isFetching}
          options={publicOptions}
          getOptionLabel={(option) => option.name}
          value={formik.values.target}
          onChange={(_, value) => {
            formik.setFieldValue("target", value);
          }}
          error={!!formik.errors.target || !!formik.errors.target}
          helperText={
            (!!formik.errors.target && (formik.errors.target as string)) ||
            (!!formik.errors.target && (formik.errors.target as string))
          }
        />
      )}

      <Stack direction="row" spacing={2}>
        <TextField
          label="Nome"
          onChange={({ target: { value } }) =>
            formik.setFieldValue("name", value)
          }
          fullWidth
          size="small"
          value={formik.values.name}
          error={!!formik.errors.name}
          helperText={!!formik.errors.name && formik.errors.name}
        />
        <TextField
          label="Quantidade de premiados"
          type="number"
          onChange={({ target: { value } }) =>
            formik.setFieldValue("top_ranking", value)
          }
          fullWidth
          size="small"
          value={formik.values.top_ranking}
          error={!!formik.errors.top_ranking}
          helperText={!!formik.errors.top_ranking && formik.errors.top_ranking}
        />
      </Stack>
    </Stack>
  );
}
